import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { BadgePercent, Save, PlayCircle, RefreshCw, CheckCircle2, AlertTriangle, IndianRupee } from "lucide-react";

export default function AdminPayments() {
  const [rates, setRates] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [savingCat, setSavingCat] = useState("");
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const load = async () => {
    const [a, b] = await Promise.all([
      axios.get(`${API}/admin/commission-rates`),
      axios.get(`${API}/admin/payouts`),
    ]);
    setRates(a.data.items);
    setPayouts(b.data.items);
  };
  useEffect(() => { load(); }, []);

  const save = async (cat, pct) => {
    setSavingCat(cat);
    try { await axios.patch(`${API}/admin/commission-rates`, { category: cat, platform_pct: Number(pct) }); }
    finally { setSavingCat(""); load(); }
  };

  const runWeekly = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/admin/payouts/run-weekly`);
      setLastRun(data);
      load();
    } finally { setRunning(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-payments-page">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold inline-flex items-center gap-1"><BadgePercent className="w-4 h-4"/>Marketplace economics</div>
        <h1 className="font-heading text-3xl font-extrabold mt-1">Commission rates & payouts</h1>
        <p className="text-ink-soft text-sm">Set the platform fee for each service category. Weekly payout batch runs every Monday — trigger it manually below.</p>
      </header>

      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
        <h2 className="font-heading text-xl font-bold mb-3">Platform fee by category</h2>
        <div className="space-y-2">
          {rates.map(r => (
            <CommissionRow key={r.category} rate={r} onSave={save} saving={savingCat === r.category}/>
          ))}
        </div>
      </section>

      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid="weekly-payout-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-heading text-xl font-bold inline-flex items-center gap-2"><PlayCircle className="w-5 h-5 text-brand"/>Weekly payout batch</h2>
            <p className="text-ink-soft text-sm">Aggregates all <b>pending</b> earnings per partner → moves to <b>in_payout</b> / <b>paid</b>.</p>
          </div>
          <button onClick={runWeekly} disabled={running} data-testid="run-weekly-btn" className="bg-brand text-white rounded-full px-5 py-2.5 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60 hover:bg-brand-hover">
            {running ? <><RefreshCw className="w-4 h-4 animate-spin"/>Running…</> : <><PlayCircle className="w-4 h-4"/>Run now</>}
          </button>
        </div>
        {lastRun && (
          <div className="mt-4 bg-leaf/30 border border-leaf rounded-2xl p-3 text-sm">
            <CheckCircle2 className="inline w-4 h-4 mr-1"/> Processed {lastRun.processed} partner(s). Razorpay live: <b>{String(lastRun.razorpay_live)}</b> · Stripe live: <b>{String(lastRun.stripe_live)}</b>
            {lastRun.payouts?.[0]?.status === "pending_activation" && (
              <div className="mt-2 text-xs"><AlertTriangle className="inline w-3.5 h-3.5 mr-1 text-brand"/>{lastRun.payouts[0].reason_if_pending}</div>
            )}
          </div>
        )}
      </section>

      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
        <h2 className="font-heading text-xl font-bold mb-3">Recent payouts</h2>
        {payouts.length === 0 ? (
          <p className="text-ink-soft text-sm">No payouts yet. Run the weekly batch to see partner-by-partner breakdown.</p>
        ) : (
          <div className="divide-y divide-line">
            {payouts.map(p => (
              <div key={p.payout_id} className="py-3 flex items-center justify-between gap-3 flex-wrap text-sm">
                <div>
                  <div className="font-bold">₹{p.amount} → user <code className="text-xs">{p.user_id.slice(-8)}</code></div>
                  <div className="text-xs text-ink-soft">{new Date(p.created_at).toLocaleString()} · {p.earnings_count} earnings · {p.gateway}</div>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${p.status === "paid" ? "bg-leaf/40 text-ink" : "bg-sun/40 text-ink"}`}>{p.status.replace(/_/g, " ")}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <DemoDataCleaner/>
    </div>
  );
}

function DemoDataCleaner() {
  const [status, setStatus] = useState(null);
  const [purging, setPurging] = useState(false);
  const [done, setDone] = useState(null);
  const load = () => axios.get(`${API}/admin/seed/status`).then(r => setStatus(r.data));
  useEffect(() => { load(); }, []);

  const purge = async () => {
    if (!window.confirm("This will permanently remove ALL seeded demo data (vets, NGOs, adoption listings, groomers, walkers, vans, foster pets, etc.). Real users' data is NOT touched. Continue?")) return;
    setPurging(true);
    try {
      const { data } = await axios.post(`${API}/admin/seed/purge`);
      setDone(data);
      load();
    } finally { setPurging(false); }
  };

  if (!status) return null;

  return (
    <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid="seed-cleaner-card">
      <h2 className="font-heading text-xl font-bold inline-flex items-center gap-2">Clear demo data</h2>
      <p className="text-ink-soft text-sm mt-1">When real users start signing up, remove all seeded vets, NGOs, groomers, walkers, vans and sample listings in one click. Real user data is never touched.</p>

      {status.seed_disabled ? (
        <div className="mt-4 bg-leaf/40 border border-leaf rounded-2xl p-3 text-sm inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-green"/> Demo data has been removed. Startup re-seeding is disabled.</div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {Object.entries(status.removable_counts).map(([k, v]) => (
              <div key={k} className="bg-surface-muted/60 rounded-xl p-2.5">
                <div className="font-heading text-lg font-extrabold">{v}</div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-ink-soft">{k.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
          <button onClick={purge} disabled={purging || status.total_removable === 0} data-testid="purge-demo-btn" className="mt-4 bg-brand text-white rounded-full px-5 py-2.5 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-40 hover:bg-brand-hover">
            {purging ? <><RefreshCw className="w-4 h-4 animate-spin"/>Removing…</> : <>Remove {status.total_removable} demo records</>}
          </button>
        </>
      )}

      {done && (
        <div className="mt-4 bg-leaf/30 border border-leaf rounded-2xl p-3 text-sm">
          <CheckCircle2 className="inline w-4 h-4 mr-1 text-brand-green"/> Cleared <b>{done.total_deleted}</b> demo rows. Startup re-seeding is now disabled — you'll only see real users from here on.
        </div>
      )}
    </section>
  );
}

function CommissionRow({ rate, onSave, saving }) {
  const [pct, setPct] = useState(rate.platform_pct);
  useEffect(() => setPct(rate.platform_pct), [rate.platform_pct]);
  const dirty = Number(pct) !== Number(rate.platform_pct);
  return (
    <div className="flex items-center gap-3 py-2 border-b border-line/50 last:border-0" data-testid={`commission-row-${rate.category}`}>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-extrabold capitalize">{rate.category}</div>
        <div className="text-xs text-ink-soft truncate">{rate.description || ""}</div>
      </div>
      <div className="flex items-center gap-1">
        <input type="number" min="0" max="50" step="0.5" value={pct} onChange={e => setPct(e.target.value)} data-testid={`commission-input-${rate.category}`} className="w-20 text-right border border-line rounded-full px-3 py-1.5 text-sm font-bold focus:border-brand outline-none"/>
        <span className="text-sm font-bold text-ink-soft">%</span>
      </div>
      <button disabled={!dirty || saving} onClick={() => onSave(rate.category, pct)} data-testid={`commission-save-${rate.category}`} className="rounded-full px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed bg-brand text-white hover:bg-brand-hover">
        <Save className="w-3 h-3"/> Save
      </button>
    </div>
  );
}
